/**
 * Combined camera manufacturers list.
 * Separated from types.ts to allow tree-shaking / lazy-loading of the
 * 40K-line cameras.ts database — it should only be pulled into chunks
 * that actually need the full model list.
 */
import type { CameraManufacturer } from '@/components/cctv-designer-v2/types';
import { CAMERA_MANUFACTURERS_EXTENDED, GANZ_SECURITY_CAMERAS } from '@/data/cameras';
import { applySpecCorrections } from '@/data/specCorrections.generated';
import { applyPublishedZoom } from '@/data/publishedZoom';
import { GENERIC_LPR, GENERIC_THERMAL } from '@/data/genericCameraModels';

const GENERIC_MODELS: CameraManufacturer[] = [
  {
    name: 'Generic',
    models: [
      { name: 'Generic Dome', hfov: 90, range: 20, irRange: 30, type: 'dome' },
      { name: 'Generic Bullet', hfov: 60, range: 50, irRange: 50, type: 'bullet' },
      { name: 'Generic PTZ', hfov: 45, range: 30, irRange: 150, type: 'ptz' },
      { name: 'Generic Panoramic 180°', hfov: 180, range: 15, irRange: 15, type: 'panoramic' },
      GENERIC_THERMAL,
      GENERIC_LPR,
    ],
  },
];

/**
 * Verified specifications overlaid on the hand-written list.
 *
 * `cameras.ts` came from scraping and cannot tell a manufacturer value from a
 * default: 42 217 of its 65 530 rows carry hfov 106 / range 20 m as if they
 * were measured. The overlay in specCorrections.generated.ts replaces the ones
 * that were checked against the maker's own datasheet, and stamps `specSrc` so
 * the product can say which is which. Same shape as lprIndex.ts: a generated
 * file merged at the point of use, leaving cameras.ts hand-editable.
 *
 * generate-catalog-shards.mjs applies the same overlay, so the lazily fetched
 * client shards and this list never disagree.
 */
export const CAMERA_MANUFACTURERS: CameraManufacturer[] = applyPublishedZoom(applySpecCorrections(
  [...GENERIC_MODELS, GANZ_SECURITY_CAMERAS, ...CAMERA_MANUFACTURERS_EXTENDED],
  // Manufacturers cameras.ts never carried. The forge crawls sites outside the
  // original scrape, and a verified model of a new maker has no brand to join.
  { addNewBrands: true },
));
